/**
 * @author       kevin.chau@zumper.com (Kevin Chau)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  Helper for use with React.memo to ensure only props used by the component are compared.
 */

import { hasProperty } from '@zcode/utils'
import { shallowEqual } from 'react-redux'

export const createAllowedPropsAreEqual = <Props extends object>(
  equalProps?: ReadonlyArray<keyof Props>,
  shallowEqualProps?: ReadonlyArray<keyof Props>
) => {
  // always true if no allowed props
  if (!equalProps?.length && !shallowEqualProps?.length) {
    return () => true
  }

  return (prevProps: Props, nextProps: Props) => {
    if (equalProps) {
      for (const propName of equalProps) {
        if (prevProps[propName] !== nextProps[propName]) {
          return false
        }
      }
    }
    if (shallowEqualProps) {
      for (const shallowPropName of shallowEqualProps) {
        // if the propName is an immutable, we want to use immutable methods to compare
        const prev = prevProps[shallowPropName]
        const next = nextProps[shallowPropName]

        if (
          prev &&
          next &&
          typeof prev === 'object' &&
          typeof next === 'object' &&
          hasProperty(prev, 'equals') &&
          hasProperty(next, 'equals') &&
          typeof prev.equals === 'function' &&
          !prev.equals(next)
        ) {
          return false
        }
        if (!shallowEqual(prev, next)) {
          return false
        }
      }
    }
    return true
  }
}
